import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  FlatList,
  Image,
  RefreshControl,
  TextInput,
  useWindowDimensions,
  Animated,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { InsetModal as Modal } from '../components/InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { StorefrontHeader } from '../components/storefront/StorefrontHeader';
import { useCartStore } from '../store/cartStore';
import { useStorefront } from '../hooks/useStorefront';
import { useThemedRTLStyles } from '../hooks/useThemedRTLStyles';
import { useLocale } from '../contexts/LocaleContext';
import { colors, typography } from '../theme';
import { getImageUrlSync, getApiBaseURL } from '../utils/imageProxy';
import { getCurrencySymbol } from '../utils/currencies';
import { ProductViewContent } from '../components/products';
import { businessCurrency } from '../utils/businessDefaults';

const GAP = 12;
const H_PAD = 14;
const MAX_CONTENT_WIDTH = 1180;
const PAGE_SIZE = 20;

const filterAvailableProducts = (list: any[]) =>
  list.filter((p: any) => {
    if (!p.inventoryTracking) return true;
    if (p.availability === 'out_of_stock' && (p.inventoryQuantity ?? 0) <= 0) return false;
    return true;
  });

const productImage = (item: any): string | null => {
  if (item.images?.length > 0) return item.images.find((i: any) => i.url)?.url || null;
  return item.primaryImage || null;
};

const formatMoney = (currency: string, amount: number) => `${currency} ${Number(amount || 0).toFixed(2)}`;

// ─────────────────────────────────────────────────────────────────────────────
// Product card
// ─────────────────────────────────────────────────────────────────────────────

interface ProductCardProps {
  item: any;
  width: number;
  quantity: number;
  getImageUrl: (url: string) => string;
  onPress: () => void;
  onAdd: () => void;
  onIncrease: () => void;
  onDecrease: () => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ item, width, quantity, getImageUrl, onPress, onAdd, onIncrease, onDecrease }) => {
  const s = useThemedRTLStyles(getCardStyles);
  const { t } = useLocale();
  const img = productImage(item);
  const currency = getCurrencySymbol(item.currency || businessCurrency());
  const salePrice = item.salePrice && item.salePrice < item.price ? item.salePrice : null;
  const displayPrice = salePrice ?? item.price ?? 0;
  const discountPct = salePrice ? Math.round(((item.price - salePrice) / item.price) * 100) : 0;
  const isOutOfStock = item.availability === 'out_of_stock';
  const needsOptions = item.hasVariants && item.variants?.length > 0;
  const minQty = Number(item.minimumOrderQuantity) || 0;

  return (
    <View style={[s.card, { width }]}>
      <TouchableOpacity activeOpacity={0.9} onPress={onPress} accessibilityRole="button" accessibilityLabel={item.name}>
        <View style={[s.imageWrap, { height: width }]}>
          {img ? (
            Platform.OS === 'web' ? (
              <img
                src={getImageUrl(img)}
                alt={item.name}
                loading="lazy"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' } as any}
              />
            ) : (
              <Image source={{ uri: getImageUrl(img) }} style={s.image} resizeMode="cover" />
            )
          ) : (
            <View style={s.imagePlaceholder}>
              <Ionicons name="image-outline" size={30} color={colors.text.disabled as string} />
            </View>
          )}
          {discountPct > 0 && (
            <View style={s.discountBadge}>
              <Text style={s.discountText}>−{discountPct}%</Text>
            </View>
          )}
          {isOutOfStock && (
            <View style={s.stockOverlay}>
              <Text style={s.stockOverlayText}>{t('storefront.outOfStock')}</Text>
            </View>
          )}
        </View>
        <View style={s.info}>
          {item.brand ? <Text style={s.brand} numberOfLines={1}>{item.brand}</Text> : null}
          <Text style={s.name} numberOfLines={2}>{item.name}</Text>
          <View style={s.priceRow}>
            <Text style={s.price} numberOfLines={1}>{formatMoney(currency, displayPrice)}</Text>
            {salePrice ? <Text style={s.originalPrice} numberOfLines={1}>{formatMoney(currency, item.price)}</Text> : null}
          </View>
          {minQty > 1 ? <Text style={s.minQty}>{t('storefront.minQty', { count: minQty })}</Text> : null}
        </View>
      </TouchableOpacity>

      <View style={s.actionWrap}>
        {isOutOfStock ? (
          <View style={[s.addBtn, s.addBtnDisabled]}>
            <Text style={s.addBtnTextDisabled}>{t('storefront.outOfStock')}</Text>
          </View>
        ) : quantity > 0 && !needsOptions ? (
          <View style={s.qtyRow}>
            <TouchableOpacity style={s.qtyBtn} onPress={onDecrease} hitSlop={6} accessibilityLabel="-">
              <Ionicons name={quantity <= Math.max(1, minQty) ? 'trash-outline' : 'remove'} size={18} color={colors.primary as string} />
            </TouchableOpacity>
            <Text style={s.qtyText}>{quantity}</Text>
            <TouchableOpacity style={s.qtyBtn} onPress={onIncrease} hitSlop={6} accessibilityLabel="+">
              <Ionicons name="add" size={18} color={colors.primary as string} />
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity style={s.addBtn} onPress={onAdd} activeOpacity={0.85}>
            <Ionicons name={needsOptions ? 'options-outline' : 'add'} size={16} color={colors.text.onPrimary as string} />
            <Text style={s.addBtnText} numberOfLines={1}>
              {needsOptions ? t('storefront.chooseOptions') : t('storefront.addToCart')}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const getCardStyles = () =>
  StyleSheet.create({
    card: {
      backgroundColor: colors.background.surface,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: colors.border,
      overflow: 'hidden',
    },
    imageWrap: { width: '100%', backgroundColor: colors.background.secondary, overflow: 'hidden', position: 'relative' },
    image: { width: '100%', height: '100%' },
    imagePlaceholder: { flex: 1, alignItems: 'center', justifyContent: 'center' },
    discountBadge: {
      position: 'absolute',
      top: 8,
      left: 8,
      backgroundColor: colors.error,
      borderRadius: 8,
      paddingHorizontal: 7,
      paddingVertical: 3,
    },
    discountText: { color: '#fff', fontSize: 11, fontFamily: typography.fontFamily.bold, textAlign: 'center' },
    stockOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.45)',
      alignItems: 'center',
      justifyContent: 'center',
    },
    stockOverlayText: { color: '#fff', fontSize: 12, fontFamily: typography.fontFamily.bold, textAlign: 'center' },
    info: { paddingHorizontal: 10, paddingTop: 8, minHeight: 78 },
    brand: { fontSize: 10, fontFamily: typography.fontFamily.semibold, color: colors.text.secondary, letterSpacing: 0.4 },
    name: { fontSize: 13, lineHeight: 19, minHeight: 38, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
    priceRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' },
    price: { fontSize: 14, fontFamily: typography.fontFamily.bold, color: colors.text.primary },
    originalPrice: { fontSize: 11, color: colors.text.secondary, textDecorationLine: 'line-through' },
    minQty: { fontSize: 11, marginTop: 2, fontFamily: typography.fontFamily.semibold, color: colors.primary },
    actionWrap: { padding: 10 },
    addBtn: {
      height: 38,
      borderRadius: 10,
      backgroundColor: colors.primary,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
      paddingHorizontal: 6,
    },
    addBtnText: { fontSize: 12, fontFamily: typography.fontFamily.bold, color: colors.text.onPrimary, textAlign: 'center' },
    addBtnDisabled: { backgroundColor: colors.background.disabled },
    addBtnTextDisabled: { fontSize: 12, fontFamily: typography.fontFamily.semibold, color: colors.text.disabled, textAlign: 'center' },
    qtyRow: {
      height: 38,
      borderRadius: 10,
      borderWidth: 1.5,
      borderColor: colors.primary,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: colors.primaryTransparent,
    },
    qtyBtn: { width: 38, height: 36, alignItems: 'center', justifyContent: 'center' },
    qtyText: { fontSize: 15, fontFamily: typography.fontFamily.bold, color: colors.text.primary, minWidth: 24, textAlign: 'center' },
  });

// ─────────────────────────────────────────────────────────────────────────────
// Screen
// ─────────────────────────────────────────────────────────────────────────────

export const CatalogViewScreen: React.FC = () => {
  const s = useThemedRTLStyles(getStyles);
  const { t } = useLocale();
  const insets = useSafeAreaInsets();
  const { params, go } = useStorefront();
  const { addItem, getItemQuantity, updateQuantity, removeItem, totalItems, totalPrice, items: cartItems } = useCartStore();
  const { width: windowWidth } = useWindowDimensions();

  const { businessId, catalogId, productId, itemId } = params;
  const isSingleProductMode = !!productId && !catalogId;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [catalog, setCatalog] = useState<any>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [itemConfig, setItemConfig] = useState<any>(null);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  // A message key, translated at render — the link's language can switch
  // the locale after the first load attempt.
  const [error, setError] = useState<'storefront.notFound' | 'storefront.loadFailed' | 'storefront.checkout.missingStore' | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'slider'>('grid');
  const [sliderIndex, setSliderIndex] = useState(0);
  const [apiBaseURL, setApiBaseURL] = useState<string | null>(null);
  const [previewProduct, setPreviewProduct] = useState<any>(null);
  const [query, setQuery] = useState('');
  const [justAdded, setJustAdded] = useState(false);
  const addedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sliderRef = useRef<FlatList>(null);
  const cartBarAnim = useRef(new Animated.Value(0)).current;

  const contentWidth = Math.min(windowWidth, MAX_CONTENT_WIDTH);
  const numColumns = contentWidth < 520 ? 2 : contentWidth < 820 ? 3 : contentWidth < 1060 ? 4 : 5;
  const cardWidth = Math.floor((contentWidth - H_PAD * 2 - GAP * (numColumns - 1)) / numColumns);

  useEffect(() => {
    getApiBaseURL().then(setApiBaseURL).catch(() => {});
  }, []);

  const getImageUrl = useCallback((url: string) => getImageUrlSync(url, apiBaseURL), [apiBaseURL]);

  const baseUrl = async () => {
    const { getCurrentApiConfig } = await import('../config/api.config');
    return getCurrentApiConfig().baseURL;
  };

  // ── Data ──────────────────────────────────────────────────────────────────
  const loadItemConfig = useCallback(async () => {
    if (!itemId || !businessId) return;
    try {
      const base = await baseUrl();
      const ep = isSingleProductMode
        ? `${base}/api/omni-channel/product/item-config/${itemId}?businessId=${businessId}`
        : `${base}/api/omni-channel/catalog/item-config/${itemId}?businessId=${businessId}`;
      const data = await (await fetch(ep)).json();
      if (data.statusCode === 1000 && data.data) setItemConfig(data.data);
    } catch {}
  }, [itemId, businessId, isSingleProductMode]);

  const loadSingleProduct = useCallback(async () => {
    if (!productId || !businessId) return setLoading(false);
    try {
      setError(null);
      const base = await baseUrl();
      const data = await (await fetch(`${base}/api/omni-channel/product/${productId}?businessId=${businessId}`)).json();
      if (data.statusCode === 1000 && data.data) setProducts([data.data]);
      else setError('storefront.notFound');
    } catch {
      setError('storefront.loadFailed');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [productId, businessId]);

  const loadCatalogPage = useCallback(
    async (page = 0, reset = false) => {
      if (!catalogId || !businessId) return setLoading(false);
      try {
        setError(null);
        if (reset) setLoading(true);
        else setLoadingMore(true);
        const base = await baseUrl();
        const skip = page * PAGE_SIZE;
        const data = await (
          await fetch(`${base}/api/omni-channel/catalog/${catalogId}/products?businessId=${businessId}&limit=${PAGE_SIZE}&skip=${skip}`)
        ).json();
        if (data.statusCode === 1000 && data.data) {
          const incoming = filterAvailableProducts(data.data.products || []);
          if (reset || page === 0) {
            setCatalog(data.data.catalogue);
            setProducts(incoming);
          } else {
            setProducts((prev) => {
              const seen = new Set(prev.map((p) => p._id));
              return [...prev, ...incoming.filter((p: any) => !seen.has(p._id))];
            });
          }
          const pagination = data.data.pagination;
          setHasMore(!!pagination && skip + PAGE_SIZE < pagination.total);
          setCurrentPage(page);
        } else {
          setError('storefront.loadFailed');
        }
      } catch {
        setError('storefront.loadFailed');
      } finally {
        setLoading(false);
        setRefreshing(false);
        setLoadingMore(false);
      }
    },
    [catalogId, businessId]
  );

  const reload = useCallback(() => {
    if (isSingleProductMode) loadSingleProduct();
    else loadCatalogPage(0, true);
  }, [isSingleProductMode, loadSingleProduct, loadCatalogPage]);

  useEffect(() => {
    if (!businessId || (!catalogId && !productId)) {
      setLoading(false);
      setError('storefront.checkout.missingStore');
      return;
    }
    loadItemConfig();
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, catalogId, productId, itemId]);

  // ── Cart ──────────────────────────────────────────────────────────────────
  const flashAdded = () => {
    setJustAdded(true);
    if (addedTimer.current) clearTimeout(addedTimer.current);
    addedTimer.current = setTimeout(() => setJustAdded(false), 1600);
  };
  useEffect(() => () => { if (addedTimer.current) clearTimeout(addedTimer.current); }, []);

  const toCartProduct = (product: any) => ({
    _id: product._id,
    name: product.name,
    price: product.price,
    salePrice: product.salePrice,
    currency: product.currency,
    primaryImage: product.primaryImage,
    images: product.images,
    brand: product.brand,
    sku: product.sku,
    availability: product.availability,
    hasVariants: product.hasVariants,
    inventoryTracking: product.inventoryTracking,
    inventoryQuantity: product.inventoryQuantity,
    minimumOrderQuantity: product.minimumOrderQuantity,
    addons: product.addons,
  });

  const stockLimit = (product: any) =>
    product.inventoryTracking && product.inventoryQuantity !== undefined ? product.inventoryQuantity : Infinity;

  const handleAdd = (product: any) => {
    if (!product._id) return;
    // Variants or required add-ons need a choice first — open the product sheet.
    const needsChoice = (product.hasVariants && product.variants?.length > 0) || product.addons?.some((a: any) => a.isRequired);
    if (needsChoice) return setPreviewProduct(product);
    // First add puts in the product's minimum order quantity.
    addItem({ productId: product._id, product: toCartProduct(product) }, Number(product.minimumOrderQuantity) || 1);
    flashAdded();
  };

  const handleIncrease = (product: any) => {
    const qty = getItemQuantity(product._id);
    if (qty >= stockLimit(product)) return;
    updateQuantity(product._id, undefined, qty + 1);
  };

  const handleDecrease = (product: any) => {
    const qty = getItemQuantity(product._id);
    // Below the product's minimum quantity the line can't be ordered — remove it.
    const min = Math.max(1, Number(product.minimumOrderQuantity) || 1);
    if (qty > min) updateQuantity(product._id, undefined, qty - 1);
    else removeItem(product._id, undefined);
  };

  const goToCart = () => go('cart');

  // ── Derived ───────────────────────────────────────────────────────────────
  const visibleProducts = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) =>
      [p.name, p.brand, p.sku].some((v) => v && String(v).toLowerCase().includes(q))
    );
  }, [products, query]);

  const gridRows = useMemo(() => {
    const rows: any[][] = [];
    for (let i = 0; i < visibleProducts.length; i += numColumns) rows.push(visibleProducts.slice(i, i + numColumns));
    return rows;
  }, [visibleProducts, numColumns]);

  const cartCurrency = getCurrencySymbol(cartItems[0]?.product.currency || products[0]?.currency || businessCurrency());
  const showCartBar = !isSingleProductMode && viewMode === 'grid' && totalItems > 0;

  useEffect(() => {
    Animated.timing(cartBarAnim, { toValue: showCartBar ? 1 : 0, duration: 220, useNativeDriver: Platform.OS !== 'web' }).start();
  }, [showCartBar, cartBarAnim]);

  const handleScroll = useCallback(
    (e: any) => {
      const { layoutMeasurement, contentOffset, contentSize } = e.nativeEvent;
      if (layoutMeasurement.height + contentOffset.y >= contentSize.height - 400 && !loadingMore && !loading && hasMore && !query) {
        loadCatalogPage(currentPage + 1, false);
      }
    },
    [loadingMore, loading, hasMore, currentPage, loadCatalogPage, query]
  );

  const title = isSingleProductMode ? products[0]?.name || t('storefront.product') : catalog?.name || t('storefront.shop');
  const subtitle = !isSingleProductMode && catalog?.description ? catalog.description : undefined;

  const header = <StorefrontHeader title={title} subtitle={subtitle} cartCount={totalItems} onCartPress={goToCart} />;

  // ── States ────────────────────────────────────────────────────────────────
  if (loading && products.length === 0) {
    return (
      <View style={s.screen}>
        {header}
        <View style={s.center}>
          <ActivityIndicator size="large" color={colors.primary as string} />
          <Text style={s.mutedText}>{t('storefront.loading')}</Text>
        </View>
      </View>
    );
  }

  if (error && products.length === 0) {
    return (
      <View style={s.screen}>
        {header}
        <View style={s.center}>
          <View style={s.stateIcon}>
            <Ionicons name="cloud-offline-outline" size={36} color={colors.text.secondary as string} />
          </View>
          <Text style={s.stateBody}>{t(error)}</Text>
          {(businessId && (catalogId || productId)) ? (
            <TouchableOpacity style={s.primaryBtn} onPress={reload}>
              <Text style={s.primaryBtnText}>{t('storefront.retry')}</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
    );
  }

  // ── Single product ────────────────────────────────────────────────────────
  if (isSingleProductMode && products.length > 0) {
    return (
      <View style={s.screen}>
        {header}
        <View style={s.centerColumn}>
          <ProductViewContent
            product={products[0]}
            apiBaseURL={apiBaseURL}
            onGoToCart={goToCart}
            containerStyle={{ flex: 1 }}
            scrollViewStyle={{ flex: 1 }}
            contentContainerStyle={{ paddingBottom: 40 }}
            showAddToCart
          />
        </View>
      </View>
    );
  }

  // ── Catalog ───────────────────────────────────────────────────────────────
  const sliderWidth = contentWidth;

  return (
    <View style={s.screen}>
      {header}

      {products.length > 0 && (
        <View style={s.toolbarWrap}>
          <View style={[s.toolbar, { maxWidth: MAX_CONTENT_WIDTH }]}>
            <View style={s.searchBox}>
              <Ionicons name="search" size={16} color={colors.text.secondary as string} />
              <TextInput
                style={s.searchInput}
                value={query}
                onChangeText={(v) => {
                  setQuery(v);
                  if (viewMode !== 'grid') setViewMode('grid');
                }}
                placeholder={t('storefront.search')}
                placeholderTextColor={colors.text.disabled as string}
                returnKeyType="search"
              />
              {query ? (
                <TouchableOpacity onPress={() => setQuery('')} hitSlop={8}>
                  <Ionicons name="close-circle" size={16} color={colors.text.secondary as string} />
                </TouchableOpacity>
              ) : null}
            </View>
            <View style={s.toggleGroup}>
              <TouchableOpacity
                style={[s.toggleBtn, viewMode === 'grid' && s.toggleBtnActive]}
                onPress={() => setViewMode('grid')}
                accessibilityLabel={t('storefront.gridView')}
              >
                <Ionicons name="grid-outline" size={16} color={(viewMode === 'grid' ? colors.text.onPrimary : colors.text.secondary) as string} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[s.toggleBtn, viewMode === 'slider' && s.toggleBtnActive]}
                onPress={() => {
                  setQuery('');
                  setViewMode('slider');
                }}
                accessibilityLabel={t('storefront.slideView')}
              >
                <Ionicons name="albums-outline" size={16} color={(viewMode === 'slider' ? colors.text.onPrimary : colors.text.secondary) as string} />
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {products.length === 0 ? (
        <View style={s.center}>
          <View style={s.stateIcon}>
            <Ionicons name="bag-outline" size={36} color={colors.text.secondary as string} />
          </View>
          <Text style={s.stateTitle}>{t('storefront.empty')}</Text>
          <Text style={s.stateBody}>{t('storefront.emptyBody')}</Text>
        </View>
      ) : viewMode === 'grid' ? (
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={[s.gridContent, { paddingBottom: (showCartBar ? 96 : 32) + insets.bottom }]}
          showsVerticalScrollIndicator={false}
          scrollEventThrottle={200}
          onScroll={handleScroll}
          keyboardShouldPersistTaps="handled"
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => {
                setRefreshing(true);
                loadCatalogPage(0, true);
              }}
              colors={[colors.primary as string]}
              tintColor={colors.primary as string}
            />
          }
        >
          <View style={[s.gridInner, { width: contentWidth }]}>
            {itemConfig?.bodyText ? <Text style={s.bodyText}>{itemConfig.bodyText}</Text> : null}
            <Text style={s.countText}>
              {visibleProducts.length === 1
                ? t('storefront.oneItem')
                : t('storefront.itemsCount', { count: `${visibleProducts.length}${hasMore && !query ? '+' : ''}` })}
            </Text>

            {visibleProducts.length === 0 && query ? (
              <Text style={[s.stateBody, { marginTop: 32 }]}>{t('storefront.noResults', { query })}</Text>
            ) : null}

            {gridRows.map((row, rowIdx) => (
              <View key={rowIdx} style={s.gridRow}>
                {row.map((item: any) => (
                  <ProductCard
                    key={item._id}
                    item={item}
                    width={cardWidth}
                    quantity={item.hasVariants ? 0 : getItemQuantity(item._id)}
                    getImageUrl={getImageUrl}
                    onPress={() => setPreviewProduct(item)}
                    onAdd={() => handleAdd(item)}
                    onIncrease={() => handleIncrease(item)}
                    onDecrease={() => handleDecrease(item)}
                  />
                ))}
                {row.length < numColumns &&
                  Array.from({ length: numColumns - row.length }).map((_, i) => <View key={`fill-${i}`} style={{ width: cardWidth }} />)}
              </View>
            ))}

            {loadingMore && <ActivityIndicator style={{ marginVertical: 20 }} color={colors.primary as string} />}
            <Text style={s.poweredBy}>{t('storefront.poweredBy')}</Text>
          </View>
        </ScrollView>
      ) : (
        <View style={{ flex: 1, alignItems: 'center' }}>
          {products.length > 1 && (
            <View style={[s.sliderNav, { width: sliderWidth }]}>
              <TouchableOpacity
                style={[s.sliderNavBtn, sliderIndex <= 0 && s.disabled]}
                disabled={sliderIndex <= 0}
                onPress={() => {
                  const i = Math.max(0, sliderIndex - 1);
                  setSliderIndex(i);
                  sliderRef.current?.scrollToIndex({ index: i, animated: true });
                }}
              >
                <DirectionalIcon name="chevron-back" size={20} color={colors.text.primary as string} />
              </TouchableOpacity>
              <Text style={s.sliderCounter}>{sliderIndex + 1} / {products.length}</Text>
              <TouchableOpacity
                style={[s.sliderNavBtn, sliderIndex >= products.length - 1 && s.disabled]}
                disabled={sliderIndex >= products.length - 1}
                onPress={() => {
                  const i = Math.min(products.length - 1, sliderIndex + 1);
                  setSliderIndex(i);
                  sliderRef.current?.scrollToIndex({ index: i, animated: true });
                }}
              >
                <DirectionalIcon name="chevron-forward" size={20} color={colors.text.primary as string} />
              </TouchableOpacity>
            </View>
          )}
          <FlatList
            ref={sliderRef}
            data={products}
            keyExtractor={(item, i) => item._id || `s-${i}`}
            horizontal
            pagingEnabled
            showsHorizontalScrollIndicator={false}
            onScroll={(e) => {
              const i = Math.round((e.nativeEvent?.contentOffset?.x || 0) / sliderWidth);
              if (i >= 0 && i < products.length && i !== sliderIndex) setSliderIndex(i);
            }}
            onEndReached={() => hasMore && !loadingMore && loadCatalogPage(currentPage + 1, false)}
            scrollEventThrottle={32}
            style={{ flex: 1, width: sliderWidth }}
            getItemLayout={(_, i) => ({ length: sliderWidth, offset: sliderWidth * i, index: i })}
            renderItem={({ item }) => (
              <View style={{ width: sliderWidth, flex: 1 }}>
                <ProductViewContent
                  product={item}
                  apiBaseURL={apiBaseURL}
                  onGoToCart={goToCart}
                  containerStyle={{ width: sliderWidth, flex: 1 }}
                  scrollViewStyle={{ flex: 1 }}
                  contentContainerStyle={{ paddingBottom: 40 }}
                  showAddToCart
                />
              </View>
            )}
          />
        </View>
      )}

      {/* Floating cart bar (grid) */}
      <Animated.View
        pointerEvents={showCartBar ? 'box-none' : 'none'}
        style={[
          s.cartBarWrap,
          {
            paddingBottom: 12 + insets.bottom,
            opacity: cartBarAnim,
            transform: [{ translateY: cartBarAnim.interpolate({ inputRange: [0, 1], outputRange: [40, 0] }) }],
          },
        ]}
      >
        <TouchableOpacity style={[s.cartBar, { maxWidth: 560 }]} onPress={goToCart} activeOpacity={0.9}>
          <View style={s.cartBarCount}>
            <Text style={s.cartBarCountText}>{totalItems}</Text>
          </View>
          <Text style={s.cartBarLabel} numberOfLines={1}>
            {justAdded ? t('storefront.added') : t('storefront.viewCart')}
          </Text>
          <Text style={s.cartBarTotal}>{formatMoney(cartCurrency, totalPrice)}</Text>
          <DirectionalIcon name="chevron-forward" size={18} color={colors.text.onPrimary as string} />
        </TouchableOpacity>
      </Animated.View>

      {/* Product sheet */}
      {Platform.OS !== 'web' ? (
        <Modal visible={!!previewProduct} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setPreviewProduct(null)}>
          {previewProduct && (
            <View style={s.sheet}>
              <View style={s.sheetHeader}>
                <Text style={s.sheetTitle} numberOfLines={1}>{previewProduct.name}</Text>
                <TouchableOpacity onPress={() => setPreviewProduct(null)} hitSlop={12} accessibilityLabel={t('storefront.close')}>
                  <Ionicons name="close" size={24} color={colors.text.primary as string} />
                </TouchableOpacity>
              </View>
              <ProductViewContent
                product={previewProduct}
                apiBaseURL={apiBaseURL}
                onAddToCart={() => {
                  setPreviewProduct(null);
                  flashAdded();
                }}
                onGoToCart={() => {
                  setPreviewProduct(null);
                  goToCart();
                }}
                containerStyle={{ flex: 1 }}
                scrollViewStyle={{ flex: 1 }}
                contentContainerStyle={{ paddingBottom: 100 }}
                showAddToCart
              />
            </View>
          )}
        </Modal>
      ) : (
        previewProduct &&
        typeof document !== 'undefined' &&
        (() => {
          const ReactDOM = require('react-dom');
          return ReactDOM.createPortal(
            <div
              style={{
                position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: windowWidth >= 760 ? 'center' : 'flex-end',
                backgroundColor: colors.background.overlay as string,
              }}
              onClick={() => setPreviewProduct(null)}
            >
              <div
                style={{
                  position: 'relative', width: '100%', maxWidth: 640, height: windowWidth >= 760 ? '86%' : '90%',
                  backgroundColor: colors.background.surface as string,
                  borderRadius: windowWidth >= 760 ? 20 : undefined,
                  borderTopLeftRadius: 20, borderTopRightRadius: 20,
                  overflow: 'hidden', display: 'flex', flexDirection: 'column',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <View style={s.sheetHeader}>
                  <Text style={s.sheetTitle} numberOfLines={1}>{previewProduct.name}</Text>
                  <TouchableOpacity onPress={() => setPreviewProduct(null)} hitSlop={12} accessibilityLabel={t('storefront.close')}>
                    <Ionicons name="close" size={24} color={colors.text.primary as string} />
                  </TouchableOpacity>
                </View>
                <ProductViewContent
                  product={previewProduct}
                  apiBaseURL={apiBaseURL}
                  onAddToCart={() => {
                    setPreviewProduct(null);
                    flashAdded();
                  }}
                  onGoToCart={() => {
                    setPreviewProduct(null);
                    goToCart();
                  }}
                  containerStyle={{ flex: 1 }}
                  scrollViewStyle={{ flex: 1 }}
                  contentContainerStyle={{ paddingBottom: 100 }}
                  showAddToCart
                />
              </div>
            </div>,
            document.body
          );
        })()
      )}
    </View>
  );
};

const getStyles = () =>
  StyleSheet.create({
    screen: {
      flex: 1,
      backgroundColor: colors.background.secondary,
      ...Platform.select({
        web: { width: '100%', maxWidth: '100%', overflowX: 'hidden' as any, height: '100dvh' as any, maxHeight: '100dvh' as any, minHeight: 0, display: 'flex' as any, flexDirection: 'column' as any },
      }),
    } as any,
    centerColumn: { flex: 1, width: '100%', maxWidth: 760, alignSelf: 'center' },
    center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32, gap: 10 },
    mutedText: { fontSize: 14, fontFamily: typography.fontFamily.primary, color: colors.text.secondary, textAlign: 'center' },
    stateIcon: {
      width: 76,
      height: 76,
      borderRadius: 38,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.background.surface,
      borderWidth: 1,
      borderColor: colors.border,
      marginBottom: 6,
    },
    stateTitle: { fontSize: 18, fontFamily: typography.fontFamily.bold, color: colors.text.primary, textAlign: 'center' },
    stateBody: { fontSize: 14, lineHeight: 21, fontFamily: typography.fontFamily.primary, color: colors.text.secondary, textAlign: 'center', maxWidth: 360 },
    primaryBtn: { marginTop: 8, backgroundColor: colors.primary, borderRadius: 12, paddingHorizontal: 24, paddingVertical: 12 },
    primaryBtnText: { fontSize: 14, fontFamily: typography.fontFamily.bold, color: colors.text.onPrimary, textAlign: 'center' },

    toolbarWrap: {
      backgroundColor: colors.background.surface,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
      alignItems: 'center',
    },
    toolbar: { width: '100%', flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: H_PAD, paddingVertical: 10 },
    searchBox: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      height: 40,
      borderRadius: 12,
      paddingHorizontal: 12,
      backgroundColor: colors.background.secondary,
      borderWidth: 1,
      borderColor: colors.border,
    },
    searchInput: {
      flex: 1,
      fontSize: 14,
      fontFamily: typography.fontFamily.primary,
      color: colors.text.primary,
      paddingVertical: 0,
      ...Platform.select({ web: { outlineStyle: 'none' } as any }),
    },
    toggleGroup: { flexDirection: 'row', gap: 6 },
    toggleBtn: {
      width: 40,
      height: 40,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.background.secondary,
      borderWidth: 1,
      borderColor: colors.border,
    },
    toggleBtnActive: { backgroundColor: colors.primary, borderColor: colors.primary },

    gridContent: { alignItems: 'center', paddingTop: 12 },
    gridInner: { paddingHorizontal: H_PAD, gap: GAP },
    gridRow: { flexDirection: 'row', gap: GAP },
    bodyText: {
      fontSize: 14,
      lineHeight: 21,
      fontFamily: typography.fontFamily.primary,
      color: colors.text.primary,
      backgroundColor: colors.background.surface,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: colors.border,
      padding: 12,
    },
    countText: { fontSize: 12, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },
    poweredBy: { fontSize: 11, color: colors.text.disabled, textAlign: 'center', paddingVertical: 16, fontFamily: typography.fontFamily.primary },

    sliderNav: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: H_PAD,
      paddingVertical: 8,
    },
    sliderNavBtn: {
      width: 36,
      height: 36,
      borderRadius: 18,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.background.surface,
      borderWidth: 1,
      borderColor: colors.border,
    },
    disabled: { opacity: 0.4 },
    sliderCounter: { fontSize: 14, fontFamily: typography.fontFamily.semibold, color: colors.text.secondary, textAlign: 'center' },

    cartBarWrap: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      paddingHorizontal: H_PAD,
      alignItems: 'center',
    },
    cartBar: {
      width: '100%',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      backgroundColor: colors.primary,
      borderRadius: 16,
      paddingHorizontal: 14,
      height: 56,
      ...Platform.select({
        ios: { shadowColor: '#000', shadowOffset: { width: 0, height: 6 }, shadowOpacity: 0.2, shadowRadius: 12 },
        android: { elevation: 8 },
        web: { boxShadow: '0 8px 24px rgba(0,0,0,0.25)' } as any,
      }),
    },
    cartBarCount: {
      minWidth: 28,
      height: 28,
      borderRadius: 14,
      paddingHorizontal: 6,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(10,15,30,0.15)',
    },
    cartBarCountText: { fontSize: 13, fontFamily: typography.fontFamily.bold, color: colors.text.onPrimary, textAlign: 'center' },
    cartBarLabel: { flex: 1, fontSize: 15, fontFamily: typography.fontFamily.bold, color: colors.text.onPrimary },
    cartBarTotal: { fontSize: 15, fontFamily: typography.fontFamily.bold, color: colors.text.onPrimary },

    sheet: { flex: 1, backgroundColor: colors.background.surface },
    sheetHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      paddingHorizontal: 18,
      paddingVertical: 14,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
      backgroundColor: colors.background.surface,
    },
    sheetTitle: { flex: 1, fontSize: 16, fontFamily: typography.fontFamily.bold, color: colors.text.primary },
  });
